import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from 'react';
import { registerPluggableViewDeclaration, unregisterPluggableView } from './pluggableViewRegistry';
import PluggableView from './PluggableView';
/**
 * @export
 * Hook to register a pluggable view and get a render function
 *
 * Simplifies pluggable view usage by eliminating duplication:
 * - Registers the view declaration once
 * - Returns a render function for usage
 *
 * @param declaration - View declaration without props (props passed to render function)
 * @returns Render function that accepts props and returns PluggableView JSX
 *
 * @example
 * ```typescript
 * const renderOtherDetails = usePluggableView({
 *     viewId: `${App}/details/other`,
 *     parentViewId: `${App}/details`,
 *     defaultPage: OtherDetails
 * });
 *
 * // Use in render:
 * return (
 *     <div>
 *         {renderOtherDetails({
 *             location: item,
 *             loading,
 *             isPreview
 *         })}
 *     </div>
 * );
 * ```
 */
export function usePluggableView(declaration) {
    useEffect(() => {
        // Register the view declaration
        registerPluggableViewDeclaration(declaration);
        // Cleanup: unregister on unmount
        return () => {
            unregisterPluggableView(declaration.viewId);
        };
    }, [declaration]);
    return (props) => _jsx(PluggableView, Object.assign({}, declaration, { props: Object.assign({}, props) }));
}
